/*
 * Larry vs Harry — "Bullitt Grid" pixel light-cycle hero game
 * Theme asset: assets/lvh-pixel-game.js
 *
 * Scoring: consecutive wins against an opponent that gets sharper and faster
 * every round. Your streak is the score.
 *
 * No backend. No endpoint, no token, no credentials, nothing to attack.
 * The personal best lives in the visitor's own browser, and the score card is
 * generated on the client as a JPEG they can save or share.
 */
(function () {
  'use strict';

  /* ---------- constants ---------- */

  var ORDER = ['up', 'right', 'down', 'left'];
  var VEC = { up: [0, -1], right: [1, 0], down: [0, 1], left: [-1, 0] };
  var OPP = { up: 'down', down: 'up', left: 'right', right: 'left' };
  var CELL = 4; // internal pixels per grid cell — the "pixel" size
  // Power-up footprint: centre plus four arms. Also the hitbox.
  var CROSS = [[0, 0], [-1, 0], [1, 0], [0, -1], [0, 1]];
  var STORE_KEY = 'lvh-grid-best';

  function leftOf(d) { return ORDER[(ORDER.indexOf(d) + 3) % 4]; }
  function rightOf(d) { return ORDER[(ORDER.indexOf(d) + 1) % 4]; }

  /* ---------- helpers ---------- */

  function attr(el, name, fallback) {
    var v = el.getAttribute('data-' + name);
    return (v === null || v === '') ? fallback : v;
  }
  function numAttr(el, name, fallback) {
    var n = parseFloat(attr(el, name, ''));
    return isNaN(n) ? fallback : n;
  }
  function boolAttr(el, name, fallback) {
    var v = attr(el, name, null);
    if (v === null) return fallback;
    return v === 'true' || v === '1';
  }

  function hexToRgb(hex) {
    hex = String(hex || '').replace('#', '').trim();
    if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    var n = parseInt(hex, 16);
    if (isNaN(n)) return [255, 255, 255];
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  function mix(hex, target, amount) {
    var a = hexToRgb(hex), b = hexToRgb(target);
    return 'rgb(' +
      Math.round(a[0] + (b[0] - a[0]) * amount) + ',' +
      Math.round(a[1] + (b[1] - a[1]) * amount) + ',' +
      Math.round(a[2] + (b[2] - a[2]) * amount) + ')';
  }

  /* Seeded PRNG (mulberry32). Everything that affects GAME STATE draws from
     this, never Math.random, so a round is fully reproducible from its seed
     plus the player's inputs. That is what makes server-side replay
     verification possible: the same engine file, fed the same seed and inputs,
     must produce the same score — so a submitted score can be recomputed
     rather than believed.

     Cosmetic randomness (wordmark construction, rewind debris) deliberately
     stays on Math.random: it never touches the grid, and seeding it would make
     repeated idle loops visibly identical. */
  function mulberry32(a) {
    return function () {
      a |= 0; a = (a + 0x6D2B79F5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  var reduceMotion = typeof window !== 'undefined' && window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- rider ---------- */

  function Rider(color, isHuman, mistake, sight) {
    this.color = color;
    this.light = mix(color, '#ffffff', 0.45);
    this.isHuman = !!isHuman;
    this.mistake = mistake;
    this.sight = sight || 400;
    this.trail = [];
    this.dir = 'right';
    this.queued = null;
    this.x = 0;
    this.y = 0;
  }

  /* ---------- game ---------- */

  function BullittGrid(root) {
    this.root = root;

    this.cfg = {
      playerColor: attr(root, 'player-color', '#ff2e88'),
      aiColor:     attr(root, 'ai-color', '#25e5ff'),
      bgColor:     attr(root, 'bg-color', '#07070d'),
      gridColor:   attr(root, 'grid-color', '#171727'),
      trailDim:    numAttr(root, 'trail-dim', 0),
      stepMs:      numAttr(root, 'step-ms', 72),
      aiMistake:   numAttr(root, 'ai-mistake', 0.10),
      colsDesktop: numAttr(root, 'cols-desktop', 136),
      colsMobile:  numAttr(root, 'cols-mobile', 70),
      maxSeconds:  numAttr(root, 'max-seconds', 40),
      glow:        boolAttr(root, 'glow', true) && !reduceMotion,
      attract:     boolAttr(root, 'attract', true) && !reduceMotion,
      gridEvery:   numAttr(root, 'grid-every', 2),   // backdrop line every N cells

      // --- escalation ---
      // Speed ramps on a curve, not a straight line: the first few rounds stay
      // slow and readable, then it tightens hard once you're committed.
      rampSpeed:   numAttr(root, 'ramp-speed', 1.2),
      rampCurve:   numAttr(root, 'ramp-curve', 1.5),
      rampFloor:   numAttr(root, 'ramp-floor', 38),
      rampSharp:   numAttr(root, 'ramp-sharp', 0.86),

      // --- logo simulation (idle state) ---
      // Not an animation: the same rider, on the same grid, at the same cell
      // size — it just follows the wordmark's centreline instead of an AI.
      logoStepMs:  numAttr(root, 'logo-step-ms', 34),
      logoHoldMs:  numAttr(root, 'logo-hold-ms', 2200),
      logoSpawnMs: numAttr(root, 'logo-spawn-ms', 420),
      logoParts:   numAttr(root, 'logo-particles', 5),
      logoScatter: numAttr(root, 'logo-scatter', 7),   // cells the fragments fly in from
      logoOutMs:      numAttr(root, 'logo-out-ms', 850),   // disintegration length
      logoOutScatter: numAttr(root, 'logo-out-scatter', 26), // how far the debris travels
      idleReturnMs: numAttr(root, 'idle-return-ms', 30000),
      // The wordmark's own two colours, kept separate exactly as drawn:
      // the white stroke and the purple accent are different design elements.
      logoLine:    attr(root, 'logo-line-color', '#eed9ea'),
      logoAccent:  attr(root, 'logo-accent-color', '#613393'),
      logoAccentOn: boolAttr(root, 'logo-accent', true),
      logoRidden:  attr(root, 'logo-ridden-color', ''),  // '' = use the white stroke colour

      // --- power-ups ---
      phaseOn:     boolAttr(root, 'phase-enabled', true),
      phaseEvery:  numAttr(root, 'phase-every-steps', 48),  // spawn interval
      phaseSteps:  numAttr(root, 'phase-steps', 26),        // duration once activated
      phaseColor:  attr(root, 'phase-color', '#ffe45e'),
      boostColor:  attr(root, 'boost-color', '#5effc4'),
      boostSteps:  numAttr(root, 'boost-steps', 34),
      boostScale:  numAttr(root, 'boost-scale', 0.62),      // step time multiplier
      phaseMax:    numAttr(root, 'phase-max-onboard', 2),

      // --- tutorial ---
      tutorialOn:  boolAttr(root, 'tutorial', true),
      // Mobile is a header, not a game: a swipe-driven light-cycle on a phone
      // is a bad experience and it fights the page scroll. Off by default.
      mobilePlay:  boolAttr(root, 'mobile-play', false),
      mobileMax:   numAttr(root, 'mobile-breakpoint', 749),
      tutorialMs:  numAttr(root, 'tutorial-ms', 4200),

      // --- AI ---
      aiMinSpace:  numAttr(root, 'ai-min-space', 14),  // a "mistake" still has to be survivable
      aiSight:     numAttr(root, 'ai-sight', 150),     // survival lookahead — always generous
      aiSightRamp: numAttr(root, 'ai-sight-ramp', 1.35), // multiplier per round survived
      aiPlan:      numAttr(root, 'ai-plan', 0.45),     // round-one care for territory
      aiPlanRamp:  numAttr(root, 'ai-plan-ramp', 0.10), // extra care per round
      aiSafeFloor: numAttr(root, 'ai-safe-floor', 26),  // room it insists on keeping
      aiSeek:      numAttr(root, 'ai-seek', 1.0),      // pull toward power-ups
      aiBlock:     numAttr(root, 'ai-block', 0.80),    // round-one urge to cut you off
      aiBlockRamp: numAttr(root, 'ai-block-ramp', 0.12), // extra aggression per round
      aiHuntGain:  numAttr(root, 'ai-hunt-gain', 2.5),
      aiSafeFrac:  numAttr(root, 'ai-safe-frac', 0.6),   // score per cell closer to the intercept
      aiUsesPowerups: boolAttr(root, 'ai-powerups', true),

      // --- crit save ---
      critOn:      boolAttr(root, 'crit-enabled', true),
      critSweepMs: numAttr(root, 'crit-sweep-ms', 1100),  // one full pass of the marker
      critZone:    numAttr(root, 'crit-zone', 0.26),      // starting target width (0-1)
      critShrink:  numAttr(root, 'crit-shrink', 0.62),    // multiplier per attempt
      critMaxTries: numAttr(root, 'crit-max-tries', 10),  // attempt 10 cannot be won, by code
      critZoneFinal: numAttr(root, 'crit-zone-final', 0.000001), // attempt 9: 0.0001%
      critRewind:  numAttr(root, 'crit-rewind-steps', 12),
      critPasses:  numAttr(root, 'crit-passes', 3),   // marker passes before it auto-fails
      critPrompt:  attr(root, 'crit-prompt', 'CRIT SAVE — HIT SPACE'),
      critPromptTouch: attr(root, 'crit-prompt-touch', 'CRIT SAVE — TAP NOW'),
      critRewindMs: numAttr(root, 'crit-rewind-ms', 700),
      rewindLagMs: numAttr(root, 'rewind-lag-ms', 130),   // debris trails the erase
      rewindFxMs:  numAttr(root, 'rewind-fx-ms', 420),    // how long a cell takes to blow apart
      rewindScatter: numAttr(root, 'rewind-scatter', 9),

      // --- share card ---
      shareTitle:  attr(root, 'share-title', 'MISS BULLITT'),
      shareTag:    attr(root, 'share-tag', '#missbullitthighscore'),
      shareSite:   attr(root, 'share-site', 'larryvsharry.com'),
      shareText:   attr(root, 'share-text', 'wins in a row on the Bullitt Grid.')
    };

    this.canvas = root.querySelector('[data-lvh-canvas]');
    this.glowCanvas = root.querySelector('[data-lvh-glow]');
    this.gridCanvas = root.querySelector('[data-lvh-gridlayer]');
    this.fxCanvas = root.querySelector('[data-lvh-fx]');
    this.poster = root.querySelector('[data-lvh-poster]');
    this.startBtn = root.querySelector('[data-lvh-start]');
    this.overCard = root.querySelector('[data-lvh-over]');
    this.overTitle = root.querySelector('[data-lvh-over-title]');
    this.overBody = root.querySelector('[data-lvh-over-body]');
    this.retryBtn = root.querySelector('[data-lvh-retry]');
    this.countEl = root.querySelector('[data-lvh-count]');
    this.streakEl = root.querySelector('[data-lvh-streak]');
    this.levelEl = root.querySelector('[data-lvh-level]');
    this.bestEl = root.querySelector('[data-lvh-best]');
    this.newBestEl = root.querySelector('[data-lvh-newbest]');
    this.reward = root.querySelector('[data-lvh-reward]');
    this.dpad = root.querySelector('[data-lvh-dpad]');
    this.shareBtn = root.querySelector('[data-lvh-share]');
    this.saveBtn = root.querySelector('[data-lvh-save]');
    this.statusEl = root.querySelector('[data-lvh-status]');
    this.heldEl = root.querySelector('[data-lvh-held]');
    this.tutorialEl = root.querySelector('[data-lvh-tutorial]');
    this.tutReadyBtn = root.querySelector('[data-lvh-tut-ready]');
    this.tutCloseBtn = root.querySelector('[data-lvh-tut-close]');

    if (!this.canvas) return;

    this.ctx = this.canvas.getContext('2d');
    this.gctx = this.glowCanvas ? this.glowCanvas.getContext('2d') : null;
    this.fx = this.fxCanvas ? this.fxCanvas.getContext('2d') : null;

    this.pickups = [];
    this.phaseLeft = 0;
    this.boostLeft = 0;
    this.held = null;
    this.critZone = this.cfg.critZone;
    this.critTries = 0;
    this.critHits = 0;
    this.critPerfect = 0;
    this._chain = 2166138321;
    this._shadowStreak = 0;
    this._shadowLevel = 0;

    this.mode = 'idle';   // idle | attract | countdown | playing | over
    this.streak = 0;
    this.level = 0;
    this.best = this.readBest();
    this.acc = 0;
    this.last = 0;
    this.raf = null;
    this.countdown = 0;

    this.idleOnly = !this.cfg.mobilePlay && window.innerWidth <= this.cfg.mobileMax;
    if (this.idleOnly) this.root.classList.add('is-idleonly');

    this.bindUI();
    this.paintBest();
    this.observe();
  }

  BullittGrid.prototype.bindUI = function () {
    var self = this;

    if (this.startBtn) this.startBtn.addEventListener('click', function () { self.newRun(true); });
    if (this.retryBtn) this.retryBtn.addEventListener('click', function () { self.newRun(); });
    if (this.tutReadyBtn) this.tutReadyBtn.addEventListener('click', function (e) { e.stopPropagation(); self.endTutorial(); });
    if (this.tutCloseBtn) this.tutCloseBtn.addEventListener('click', function (e) { e.stopPropagation(); self.endTutorial(); });
    if (this.tutorialEl) {
      this.tutorialEl.addEventListener('click', function (e) {
        var chip = e.target.closest('[data-lvh-dots] i');
        if (chip) {
          var dots = self.tutorialEl.querySelectorAll('[data-lvh-dots] i');
          for (var i = 0; i < dots.length; i++) if (dots[i] === chip) self.goTutorial(i);
        }
      });
    }
    if (this.shareBtn) this.shareBtn.addEventListener('click', function () { self.shareCard(); });
    if (this.saveBtn) this.saveBtn.addEventListener('click', function () { self.saveCard(); });

    // Keyboard — ONLY captured while actually playing, so we never steal
    // page scroll or trap keyboard users browsing the rest of the page.
    this._onKey = function (e) {
      if (self.idleOnly) return;
      if (e.key === 't' || e.key === 'T') { e.preventDefault(); self.toggleTutorial(); return; }
      var isSpace = e.key === ' ' || e.key === 'Spacebar' || e.key === 'Enter';
      if (self.mode === 'crit') {
        if (isSpace) { e.preventDefault(); self.critAttempt(); }
        return;
      }
      if (self.mode === 'tutorial') {
        // Deliberately NOT "any key closes" — you can page back and forth as
        // long as you like. Only Ready, the X, or T leave.
        e.preventDefault();
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') self.goTutorial(self.tutPage + 1);
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') self.goTutorial(self.tutPage - 1);
        return;
      }
      if (self.mode !== 'playing') return;
      if (isSpace) { e.preventDefault(); self.usePowerUp(); return; }
      var map = {
        ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right',
        w: 'up', s: 'down', a: 'left', d: 'right',
        W: 'up', S: 'down', A: 'left', D: 'right'
      };
      if (e.key === 'Escape') { self.abandonRun(); return; }
      var dir = map[e.key];
      if (!dir) return;
      e.preventDefault();
      self.queueTurn(self.p1, dir);
    };
    window.addEventListener('keydown', this._onKey, { passive: false });

    window.addEventListener('blur', function () {
      if (self.mode === 'playing') self.abandonRun();
    });

    var sx = 0, sy = 0, tracking = false;
    this.root.addEventListener('pointerdown', function () {
      if (self.idleOnly) return;
      if (self.mode === 'crit') self.critAttempt();

      else if (self.mode === 'playing' && self.held) self.usePowerUp();
    });

    this.canvas.addEventListener('touchstart', function (e) {
      if (self.mode !== 'playing') return;
      tracking = true;
      sx = e.touches[0].clientX;
      sy = e.touches[0].clientY;
    }, { passive: true });

    this.canvas.addEventListener('touchmove', function (e) {
      if (!tracking || self.mode !== 'playing') return;
      var dx = e.touches[0].clientX - sx;
      var dy = e.touches[0].clientY - sy;
      if (Math.abs(dx) < 24 && Math.abs(dy) < 24) return;
      e.preventDefault(); // safe: only while playing, only on the canvas
      self.queueTurn(self.p1, Math.abs(dx) > Math.abs(dy)
        ? (dx > 0 ? 'right' : 'left')
        : (dy > 0 ? 'down' : 'up'));
      tracking = false;
    }, { passive: false });

    if (this.dpad) {
      this.dpad.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-dir]');
        if (!btn) return;
        if (self.mode !== 'playing') { self.newRun(); return; }
        self.queueTurn(self.p1, btn.getAttribute('data-dir'));
      });
    }

    var t;
    window.addEventListener('resize', function () {
      clearTimeout(t);
      t = setTimeout(function () { self.maybeResize(); }, 200);
    });
  };

  /* Boot lazily: never on page load, never before it's on screen.
     This keeps the poster image as the LCP element. */
  BullittGrid.prototype.observe = function () {
    var self = this;
    var boot = function () {
      if (self.booted) return;
      self.booted = true;
      self.buildField();
      if (self.cfg.attract && window.LVH_LOGO) self.startLogo();
      else if (self.cfg.attract) self.startRound(false);
      else self.root.classList.add('is-ready');
    };

    if (!('IntersectionObserver' in window)) { boot(); return; }
    var io = new IntersectionObserver(function (entries) {
      if (!entries[0].isIntersecting) return;
      io.disconnect();
      if ('requestIdleCallback' in window) requestIdleCallback(boot, { timeout: 1200 });
      else setTimeout(boot, 200);
    }, { rootMargin: '200px' });
    io.observe(this.root);
  };

  /* ---------- field ---------- */

  BullittGrid.prototype.buildField = function () {
    // Reserve the stat stripe's height before measuring anything. The stripe is
    // an overlay pinned to the top of the hero; without this the play field
    // runs underneath it and the rider vanishes behind the numbers. Measured
    // off the live element rather than hardcoded, so it stays correct when the
    // UI scale setting changes the stripe's size.
    var stripe = this.root.querySelector('.lvhg__stats');
    var inset = stripe ? Math.ceil(stripe.getBoundingClientRect().height) : 0;
    this.root.style.setProperty('--lvh-top', inset + 'px');

    // Read the canvas, not the root — the canvas is the inset one, and rows
    // must be derived from the field we actually draw into.
    var rect = (this.canvas || this.root).getBoundingClientRect();
    var w = Math.max(rect.width, 320);
    var h = Math.max(rect.height, 240);

    var wide = window.innerWidth >= 750;
    this.cols = wide ? this.cfg.colsDesktop : this.cfg.colsMobile;

    // The wordmark only centres when the field is at least its width + 4 (see
    // startLogo). Below that the camera pans with the rider, which reads as a
    // stray pixel sliding off-screen rather than a logo. The wordmark shows on
    // every viewport, so guarantee it fits on every viewport — this also means
    // a stale saved grid_density from an older version can't reintroduce the
    // slide.
    if (window.LVH_LOGO) {
      this.cols = Math.max(this.cols, window.LVH_LOGO.cols + 4);
    }

    this.rows = Math.max(24, Math.round(this.cols * (h / w)));

    this.canvas.width = this.cols * CELL;
    this.canvas.height = this.rows * CELL;
    if (this.glowCanvas) {
      this.glowCanvas.width = this.canvas.width;
      this.glowCanvas.height = this.canvas.height;
    }
    if (this.fxCanvas) {
      this.fxCanvas.width = this.canvas.width;
      this.fxCanvas.height = this.canvas.height;
      this.fx.imageSmoothingEnabled = false;
    }

    this.grid = new Uint8Array(this.cols * this.rows);
    this._stamp = new Uint16Array(this.cols * this.rows);
    this._gen = 0;

    this.ctx.imageSmoothingEnabled = false;
    if (this.gctx) this.gctx.imageSmoothingEnabled = false;

    this._fieldW = w;
    this._fieldH = h;

    this.paintGridLayer(w, h);
  };

  /* The backdrop grid is drawn from the ACTUAL cell size, so its lines land
     exactly on cell boundaries. It used to be a CSS gradient at a fixed 40px,
     which had nothing to do with the game — the two only ever lined up by
     accident. Drawn once per field build; never touched during play. */
  BullittGrid.prototype.paintGridLayer = function (w, h) {
    if (!this.gridCanvas) return;

    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var g = this.gridCanvas.getContext('2d');

    this.gridCanvas.width = Math.round(w * dpr);
    this.gridCanvas.height = Math.round(h * dpr);
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, w, h);

    // One game cell in CSS pixels. The play canvas is stretched to this same
    // width, so this is exactly how wide a cell renders on screen.
    var cellW = w / this.cols;
    var cellH = h / this.rows;
    var every = Math.max(1, this.cfg.gridEvery);

    g.fillStyle = this.cfg.gridColor;
    var i;
    for (i = 0; i <= this.cols; i += every) {
      g.fillRect(Math.round(i * cellW), 0, 1, h);
    }
    for (i = 0; i <= this.rows; i += every) {
      g.fillRect(0, Math.round(i * cellH), w, 1);
    }
  };

  BullittGrid.prototype.maybeResize = function () {
    if (!this.booted) return;
    var idle = !this.cfg.mobilePlay && window.innerWidth <= this.cfg.mobileMax;
    if (idle !== this.idleOnly) {
      this.idleOnly = idle;
      this.root.classList.toggle('is-idleonly', idle);
      if (idle && this.mode !== 'logo') this.startLogo();
    }
    // Measure the same element buildField measured. Comparing the root against
    // a canvas that is now inset by the stat stripe would report a permanent
    // ~12% height difference and rebuild the field on every single resize tick.
    var rect = (this.canvas || this.root).getBoundingClientRect();
    if (Math.abs(rect.width - this._fieldW) / this._fieldW < 0.05 &&
        Math.abs(rect.height - this._fieldH) / this._fieldH < 0.05) return;
    // Whatever we were doing, keep doing it. This used to call startRound()
    // unconditionally, which tore down the wordmark on any resize — and on a
    // phone the address bar collapsing counts as a resize, so the simulation
    // was replaced by the game within a scroll or two of landing.
    var wasPlaying = this.mode === 'playing';
    var wasLogo = this.mode === 'logo';
    this.buildField();
    if (wasLogo || this.idleOnly) this.startLogo();
    else this.startRound(wasPlaying);
  };

  /* ---------- power-ups ----------
     Phase: ride through the opponent's trail. Walls and your own trail still
     kill you, so it's an opening, not immunity. */

  BullittGrid.prototype.spawnPickup = function () {
    if (!this.cfg.phaseOn) return;
    if (this.pickups.length >= this.cfg.phaseMax) return;
    for (var tries = 0; tries < 40; tries++) {
      var x = 3 + Math.floor(this.rnd() * (this.cols - 6));
      var y = 3 + Math.floor(this.rnd() * (this.rows - 6));
      // the whole cross has to be clear, not just the centre
      var clear = true, c;
      for (var k = 0; k < CROSS.length; k++) {
        c = CROSS[k];
        if (this.grid[this.idx(x + c[0], y + c[1])] !== 0) { clear = false; break; }
      }
      if (!clear) continue;
      // keep them off both riders' noses
      if (Math.abs(x - this.p1.x) + Math.abs(y - this.p1.y) < 6) continue;
      if (Math.abs(x - this.p2.x) + Math.abs(y - this.p2.y) < 6) continue;
      var kind = this.rnd() < 0.5 ? 'phase' : 'boost';
      this.pickups.push({ x: x, y: y, kind: kind, born: performance.now() });
      return;
    }
  };

  /* Collecting stows the power-up; SPACE spends it. Holding one and choosing
     the moment is a decision — auto-firing on pickup wasn't. */
  BullittGrid.prototype.takePickup = function (x, y) {
    for (var i = 0; i < this.pickups.length; i++) {
      var p = this.pickups[i], hit = false;
      for (var k = 0; k < CROSS.length; k++) {
        if (p.x + CROSS[k][0] === x && p.y + CROSS[k][1] === y) { hit = true; break; }
      }
      if (hit) {
        this.held = this.pickups[i].kind;
        this.pickups.splice(i, 1);
        this.paintHeld();
        return true;
      }
    }
    return false;
  };

  BullittGrid.prototype.usePowerUp = function () {
    if (this.mode !== 'playing' || !this.held) return;
    if (this.held === 'phase') this.phaseLeft = this.cfg.phaseSteps;
    else if (this.held === 'boost') this.boostLeft = this.cfg.boostSteps;
    this.held = null;
    this.paintHeld();
  };

  BullittGrid.prototype.paintHeld = function () {
    if (!this.heldEl) return;
    this.heldEl.textContent = this.held
      ? (this.held === 'phase' ? 'PHASE' : 'BOOST')
      : '—';
    this.heldEl.style.color = this.held
      ? (this.held === 'phase' ? this.cfg.phaseColor : this.cfg.boostColor)
      : '';
  };

  /* ---------- crit save ----------
     On a fatal move, one attempt at a timing bar. Land the marker in the zone
     and the last second of the round is rewound — using the real recorded
     trails, so it replays exactly what happened rather than approximating it.
     The zone shrinks every attempt, so it's unlimited but self-limiting. */

  /* Zone width for a given attempt (1-based).
     Geometric from critZone down to critZoneFinal across attempts 1..max-1,
     then exactly 0. The final attempt is unwinnable in code, not merely
     improbable — critAttempt refuses to score a hit on it at all, so no amount
     of timing precision or floating-point luck can pass it. This exists because
     an uncapped crit save makes the score unbounded: the zone used to floor at
     3.5% forever, which a bot hits every time. */
  BullittGrid.prototype.critZoneFor = function (attempt) {
    var max = Math.max(2, this.cfg.critMaxTries);
    if (attempt >= max) return 0;
    var a = this.cfg.critZone, b = this.cfg.critZoneFinal, n = max - 1;
    if (n < 2) return a;
    return a * Math.pow(Math.pow(b / a, 1 / (n - 1)), attempt - 1);
  };

  BullittGrid.prototype.critExhausted = function () {
    return this.critTries >= this.cfg.critMaxTries;
  };

  BullittGrid.prototype.startCritSave = function (pendingResult) {
    this.mode = 'crit';
    this.critResult = pendingResult;
    this.critT0 = performance.now();
    this.critLocked = null;
    this.critTries++;
    this.critZone = this.critZoneFor(this.critTries);
    // Zone is placed away from the edges so it's never a freebie.
    this.critCentre = 0.25 + this.rnd() * 0.5;
    this.root.classList.add('is-crit');
    var touch = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    this.showCount(touch ? this.cfg.critPromptTouch : this.cfg.critPrompt);

    // endRound() stopped the loop before we got here. Without this the bar is
    // never drawn and the rewind can never run — the game just freezes.
    this.last = 0;
    this.run();
  };

  /* If they never press anything, don't strand them: let the marker make a few
     passes, then resolve it as a miss so the run can end normally. */
  BullittGrid.prototype.critExpired = function (now) {
    return this.critLocked === null &&
      (now - this.critT0) > this.cfg.critSweepMs * 2 * this.cfg.critPasses;
  };

  BullittGrid.prototype.critResolve = function (hit) {
    var self = this;
    this.showCount(hit ? 'SAVED' : '');
    setTimeout(function () {
      self.root.classList.remove('is-crit');
      self.showCount('');
      if (hit) { self.startRewind(); }
      else {
        self.mode = 'over';
        self.root.classList.remove('is-playing');
        self.root.classList.add('is-over');
        self.endRun(self.critResult);
      }
    }, 420);
  };

  BullittGrid.prototype.critMarker = function (now) {
    // Ping-pong 0..1..0 across the bar.
    var t = ((now - this.critT0) % (this.cfg.critSweepMs * 2)) / this.cfg.critSweepMs;
    return t <= 1 ? t : 2 - t;
  };

  /* forcedPos is supplied by a replay. The marker sweeps in wall-clock time,
     which cannot be reproduced — but it does not need to be. The only thing
     that leaves the player's hands is WHERE they locked it, so that single
     number is recorded as an input and fed back on replay. Zone and centre are
     both drawn from the seeded stream, so the verifier recomputes hit and
     accuracy itself rather than trusting either. */
  BullittGrid.prototype.critAttempt = function (forcedPos) {
    if (this.mode !== 'crit' || this.critLocked !== null) return;
    var pos = (typeof forcedPos === 'number')
      ? forcedPos : this.critMarker(performance.now());
    pos = Math.max(0, Math.min(1, pos));
    this.critLocked = pos;
    if (this.inputLog) this.inputLog.push([this.steps, 'crit', pos]);

    var half = this.critZone / 2;
    // The final attempt is a wall: no position can pass it.
    var hit = !this.critExhausted() && half > 0 &&
              Math.abs(pos - this.critCentre) <= half;

    if (hit) {
      // Dead centre pays 3 seconds, the very edge of the zone pays 1.
      var acc = 1 - Math.min(1, Math.abs(pos - this.critCentre) / half);
      this.critSeconds = 1 + 2 * acc;
      this.critAccuracy = acc;
      this.critHits = (this.critHits || 0) + 1;
      // Landing dead centre repeatedly is not something a hand does.
      if (acc > 0.999) this.critPerfect = (this.critPerfect || 0) + 1;
      this._fold(0x63 ^ Math.round(pos * 1e6));
    }
    this.critResolve(hit);
  };

  /* Rewind: walk both riders backwards along their own recorded trails,
     un-drawing as we go. Nothing is guessed — the trail arrays ARE the history
     of every move both riders made. */
  BullittGrid.prototype.startRewind = function () {
    this.mode = 'rewind';
    this.last = 0;
    this.run();
    this.rewindT0 = performance.now();
    var secs = this.critSeconds || 1;
    var want = Math.round(secs * 1000 / this.stepMs);
    this.rewindMs = Math.max(420, Math.min(1100, 300 + secs * 260));
    this.rewindFrom = Math.min(want, this.p1.trail.length - 2, this.p2.trail.length - 2);
    if (this.rewindFrom < 1) this.rewindFrom = 1;
    this.rewindDone = 0;
    this.rewindFx = [];
  };

  BullittGrid.prototype.stepRewind = function () {
    var r, c;
    var now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (!this.rewindFx) this.rewindFx = [];

    for (var k = 0; k < 2; k++) {
      r = k ? this.p2 : this.p1;
      if (r.trail.length < 2) continue;
      c = r.trail.pop();
      this.grid[this.idx(c[0], c[1])] = 0;
      this.ctx.clearRect(c[0] * CELL, c[1] * CELL, CELL, CELL);

      /* Hand the cell to the effects layer instead of just deleting it. It
         holds its colour for rewindLagMs, then blows apart with the same
         accelerate-and-fade the wordmark uses. Because the erase point keeps
         running backwards while these are still holding, the disintegration
         chases the rewind a beat behind it rather than happening all at once. */
      var ang = Math.random() * Math.PI * 2;
      this.rewindFx.push({
        x: c[0], y: c[1], col: r.color,
        vx: Math.cos(ang) * (0.6 + Math.random() * 0.9),
        vy: Math.sin(ang) * (0.6 + Math.random() * 0.9),
        born: now + this.cfg.rewindLagMs
      });

      var head = r.trail[r.trail.length - 1];
      r.x = head[0]; r.y = head[1];
    }
    // Re-derive facing from the last two cells so the resume is consistent.
    for (k = 0; k < 2; k++) {
      r = k ? this.p2 : this.p1;
      var n = r.trail.length;
      if (n >= 2) {
        var a = r.trail[n - 2], b = r.trail[n - 1];
        var dx = b[0] - a[0], dy = b[1] - a[1];
        r.dir = dx > 0 ? 'right' : dx < 0 ? 'left' : dy > 0 ? 'down' : 'up';
      }
      r.queued = null;
    }
    this.rewindDone++;
  };

  BullittGrid.prototype.finishRewind = function () {
    if (this.gctx) {
      this.gctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.gctx.drawImage(this.canvas, 0, 0);
    }
    this.mode = 'countdown';
    this.countdown = 3;
    this.showCount('3');
    this.acc = 0;
    this.last = 0;
    this.phaseLeft = 0;
    this.run();
  };

  /* ---------- logo simulation ----------
     The idle state. Same rider, same grid, same cell size as the game — it
     simply follows the wordmark's centreline instead of an AI. Cells that
     aren't on the ridden line are constructed by fragments converging on them
     as the rider passes, rather than just switching on. */

  BullittGrid.prototype.startLogo = function () {
    var D = window.LVH_LOGO;
    if (!D || !this.booted) return;

    this.mode = 'logo';
    this.stop();
    this.clearIdleTimer();

    this.logoStep = 0;
    this.logoAcc = 0;
    this.logoDone = false;
    this.logoPhase = 'in';
    this.dissolve = null;
    this.spawns = [];        // cells mid-construction
    this.solid = [];         // cells finished constructing
    this.decoAt = 0;

    // Centre the wordmark if it fits, otherwise pan with the rider.
    this.camY = Math.round((this.rows - D.rows) / 2);
    this.camX = this.cols >= D.cols + 4
      ? Math.round((this.cols - D.cols) / 2)
      : 0;
    this.logoFits = this.cols >= D.cols + 4;

    this.pickups = [];
    this.phaseLeft = 0;
    this.boostLeft = 0;
    this.held = null;
    if (this.fx) this.fx.clearRect(0, 0, this.fxCanvas.width, this.fxCanvas.height);
    this.root.classList.add('is-ready', 'is-running', 'is-logo');
    this.root.classList.remove('is-playing', 'is-attract', 'is-over');
    if (this.overCard) this.overCard.hidden = true;
    if (this.poster) this.poster.classList.add('is-hidden');
    this.showCount('');

    this.last = 0;
    this.run();
  };

  /* The wordmark doesn't pop out of existence — it comes apart. Same fragment
     language as the construction, run in reverse and all at once, with a much
     wider scatter so it reads as a blast rather than a fade. */
  BullittGrid.prototype.beginDissolve = function (now) {
    var D = window.LVH_LOGO;
    this.dissolve = [];
    var cx = D.cols / 2, cy = D.rows / 2;
    var self = this;

    function add(c, kind) {
      // push outward from the centre, plus a random kick
      var dx = (c[0] - cx) / Math.max(1, cx);
      var dy = (c[1] - cy) / Math.max(1, cy);
      var ang = Math.random() * Math.PI * 2;
      self.dissolve.push({
        x: c[0], y: c[1], k: kind,
        vx: dx * 1.7 + Math.cos(ang) * 0.9,
        vy: dy * 1.3 + Math.sin(ang) * 0.9,
        d: Math.random() * 0.22            // slight stagger so it isn't a wall
      });
    }
    var i;
    for (i = 0; i <= this.logoStep && i < D.path.length; i++) add(D.path[i], D.pathKind[i]);
    if (D.fill) {
      var fshown = D.fillCount[Math.min(this.logoStep, D.path.length)];
      for (i = 0; i < fshown; i++) add(D.fill[i], D.fillKind[i]);
    }
    for (i = 0; i < this.solid.length; i++) add(this.solid[i], this.solid[i][2]);

    this.dissolveT0 = now;
    this.logoPhase = 'out';
  };

  BullittGrid.prototype.paintDissolve = function (now) {
    var ctx = this.ctx, S = CELL;
    var t = (now - this.dissolveT0) / this.cfg.logoOutMs;
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    var LINE = this.cfg.logoLine, ACCENT = this.cfg.logoAccent;
    var spread = this.cfg.logoOutScatter;
    for (var i = 0; i < this.dissolve.length; i++) {
      var f = this.dissolve[i];
      var ft = (t - f.d) / (1 - f.d);
      if (ft <= 0) ft = 0;
      if (ft >= 1) continue;
      var e = ft * ft;                       // accelerate away
      ctx.globalAlpha = 1 - ft;
      ctx.fillStyle = f.k === 1 ? ACCENT : LINE;
      ctx.fillRect(
        Math.round((f.x + this.camX) * S + f.vx * spread * S * e),
        Math.round((f.y + this.camY) * S + f.vy * spread * S * e),
        S, S
      );
    }
    ctx.globalAlpha = 1;

    if (this.gctx) {
      this.gctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.gctx.drawImage(this.canvas, 0, 0);
    }
    return t >= 1;
  };

  BullittGrid.prototype.logoCam = function () {
    var D = window.LVH_LOGO;
    if (this.logoFits) return;
    // Keep the rider around a third of the way across while panning.
    var want = Math.round(this.cols * 0.35) - this.logoX();
    this.camX = Math.max(Math.min(want, 0), this.cols - D.cols);
  };

  BullittGrid.prototype.logoX = function () {
    var D = window.LVH_LOGO;
    return D.path[Math.min(this.logoStep, D.path.length - 1)][0];
  };

  BullittGrid.prototype.stepLogo = function (now) {
    var D = window.LVH_LOGO;
    this.logoStep++;

    if (this.logoStep >= D.path.length) {
      this.logoStep = D.path.length - 1;
      this.logoDone = true;
      this.logoDoneAt = now;
      return;
    }

    // Everything the rider has just drawn level with starts constructing.
    var target = D.revealCount[this.logoStep];
    while (this.decoAt < target) {
      var cell = D.deco[this.decoAt++];          // [x, y, kind] 0 = stroke, 1 = accent
      if (cell[2] === 1 && !this.cfg.logoAccentOn) continue;
      var parts = [];
      for (var i = 0; i < this.cfg.logoParts; i++) {
        var ang = Math.random() * Math.PI * 2;
        var dist = (0.4 + Math.random() * 0.6) * this.cfg.logoScatter;
        parts.push({
          ox: Math.cos(ang) * dist,
          oy: Math.sin(ang) * dist * 0.6,
          d: Math.random() * 0.35              // stagger within the burst
        });
      }
      this.spawns.push({ x: cell[0], y: cell[1], k: cell[2] || 0, t0: now, parts: parts });
    }
  };

  BullittGrid.prototype.paintLogo = function (now) {
    var D = window.LVH_LOGO, ctx = this.ctx;
    var S = CELL;
    this.logoCam();

    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Two colours, exactly as the artwork separates them.
    var LINE = this.cfg.logoLine;
    var ACCENT = this.cfg.logoAccent;
    var RIDDEN = this.cfg.logoRidden || LINE;
    var i, c;

    // The ridden stroke. Every cell is drawn in ITS OWN class colour, not in a
    // "ridden" colour — the artwork decides, not the rider's route.
    for (i = 0; i <= this.logoStep; i++) {
      c = D.path[i];
      var pk = D.pathKind ? D.pathKind[i] : 0;
      ctx.fillStyle = pk === 1 ? ACCENT : RIDDEN;
      ctx.fillRect((c[0] + this.camX) * S, (c[1] + this.camY) * S, S, S);
    }

    // Rest of the SAME continuous line — appears with the stroke, no particles.
    // These were previously treated as decoration, which is why parts of the
    // main line looked like they were being constructed.
    if (D.fill) {
      var fshown = D.fillCount[Math.min(this.logoStep, D.path.length)];
      for (i = 0; i < fshown; i++) {
        c = D.fill[i];
        ctx.fillStyle = D.fillKind[i] === 1 ? ACCENT : LINE;
        ctx.fillRect((c[0] + this.camX) * S, (c[1] + this.camY) * S, S, S);
      }
    }

    // constructed cells, each in its own colour
    for (i = 0; i < this.solid.length; i++) {
      c = this.solid[i];
      ctx.fillStyle = c[2] === 1 ? ACCENT : LINE;
      ctx.fillRect((c[0] + this.camX) * S, (c[1] + this.camY) * S, S, S);
    }

    // cells still being built — fragments converging on the target
    var dur = this.cfg.logoSpawnMs;
    var frag = Math.max(1, Math.round(S / 2));
    for (i = this.spawns.length - 1; i >= 0; i--) {
      var sp = this.spawns[i];
      var age = (now - sp.t0) / dur;
      if (age >= 1) {
        this.solid.push([sp.x, sp.y, sp.k]);
        this.spawns.splice(i, 1);
        continue;
      }
      var bx = (sp.x + this.camX) * S, by = (sp.y + this.camY) * S;
      var fragCol = sp.k === 1 ? ACCENT : LINE;
      for (var p = 0; p < sp.parts.length; p++) {
        var q = sp.parts[p];
        var t = (age - q.d) / (1 - q.d);
        if (t <= 0) continue;
        t = t > 1 ? 1 : t;
        var e = 1 - Math.pow(1 - t, 3);              // ease toward the cell
        ctx.globalAlpha = 0.35 + 0.65 * e;
        ctx.fillStyle = fragCol;
        ctx.fillRect(
          Math.round(bx + q.ox * S * (1 - e)),
          Math.round(by + q.oy * S * (1 - e)),
          frag, frag
        );
      }
      ctx.globalAlpha = 1;
    }

    // the rider
    c = D.path[this.logoStep];
    ctx.fillStyle = this.cfg.playerColor;
    ctx.fillRect((c[0] + this.camX) * S, (c[1] + this.camY) * S, S, S);

    if (this.gctx) {
      this.gctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.gctx.drawImage(this.canvas, 0, 0);
    }
  };

  /* ---------- idle return ---------- */

  BullittGrid.prototype.clearIdleTimer = function () {
    if (this._idle) { clearTimeout(this._idle); this._idle = null; }
  };

  BullittGrid.prototype.armIdleTimer = function () {
    var self = this;
    this.clearIdleTimer();
    if (!this.cfg.attract || !window.LVH_LOGO || !this.cfg.idleReturnMs) return;
    this._idle = setTimeout(function () {
      if (self.mode === 'playing' || self.mode === 'countdown') return;
      self.startLogo();
    }, this.cfg.idleReturnMs);
  };

  /* ---------- run / round lifecycle ---------- */

  /* ---------- tutorial ----------
     Shown once when you press Start, not before every retry. Any key or tap
     skips it; otherwise it clears itself and the round begins. */

  /* Two pages: controls, then power-ups. Any key or tap advances; the last
     page starts the round. T reopens it at any time, including mid-round —
     the round is paused and resumes on a countdown. */

  BullittGrid.prototype.startTutorial = function (page, returnTo) {
    this.tutReturn = returnTo || null;
    this.mode = 'tutorial';
    this.tutPage = page || 0;
    this.root.classList.add('is-tutorial');
    if (this.tutorialEl) this.tutorialEl.hidden = false;
    this.paintTutorial();
    this.stop();
  };

  BullittGrid.prototype.paintTutorial = function () {
    if (!this.tutorialEl) return;
    var pages = this.tutorialEl.querySelectorAll('[data-lvh-page]');
    var dots = this.tutorialEl.querySelectorAll('[data-lvh-dots] i');
    var i;
    for (i = 0; i < pages.length; i++) pages[i].hidden = (i !== this.tutPage);
    // The page number lives in the markup's data-n; only the state changes here.
    for (i = 0; i < dots.length; i++) dots[i].className = (i === this.tutPage) ? 'on' : '';
    this.tutPages = pages.length;
  };

  BullittGrid.prototype.goTutorial = function (page) {
    if (this.mode !== 'tutorial') return;
    var last = (this.tutPages || 2) - 1;
    this.tutPage = Math.max(0, Math.min(last, page));
    this.paintTutorial();
  };

  BullittGrid.prototype.endTutorial = function () {
    if (this.mode !== 'tutorial') return;
    this.root.classList.remove('is-tutorial');
    if (this.tutorialEl) this.tutorialEl.hidden = true;
    this.tutorialSeen = true;

    if (this.tutReturn === 'playing') {      // reopened mid-round: count back in
      this.tutReturn = null;
      this.mode = 'countdown';
      this.countdown = 3;
      this.showCount('3');
      this.acc = 0; this.last = 0;
      this.run();
      return;
    }
    if (this.tutReturn === 'logo') { this.tutReturn = null; this.startLogo(); return; }
    this.tutReturn = null;
    this.startRound(true);
  };

  /* T from anywhere. Mid-round it pauses; elsewhere it returns you where you were. */
  BullittGrid.prototype.toggleTutorial = function () {
    if (this.mode === 'tutorial') { this.endTutorial(); return; }
    if (this.mode === 'crit' || this.mode === 'rewind') return;
    var back = (this.mode === 'playing' || this.mode === 'countdown') ? 'playing'
             : (this.mode === 'logo' || this.mode === 'attract') ? 'logo' : null;
    this.startTutorial(0, back);
  };

  BullittGrid.prototype.newRun = function (fromStart) {
    if (this.idleOnly) return;                 // phone: header only
    this.clearIdleTimer();
    this.pickups = [];
    this.phaseLeft = 0;
    this.boostLeft = 0;
    this.aiPhaseLeft = 0;
    this.aiBoostLeft = 0;
    this.held = null;
    this.paintHeld();
    this.critZone = this.cfg.critZone;
    this.critTries = 0;
    this.root.classList.remove('is-logo', 'is-over');
    this.streak = 0;
    this.level = 0;
    this.setStatus('');
    if (this.newBestEl) this.newBestEl.hidden = true;
    if (this.overCard) this.overCard.hidden = true;

    if (fromStart && this.cfg.tutorialOn) { this.startTutorial(0); return; }
    this.startRound(true);
  };

  BullittGrid.prototype.abandonRun = function () {
    this.showCount('');
    this.streak = 0;
    this.level = 0;
    if (this.cfg.attract && window.LVH_LOGO) { this.startLogo(); }
    else if (this.cfg.attract) { this.startRound(false); }
    else {
      this.mode = 'idle';
      this.stop();
      this.root.classList.remove('is-playing', 'is-running', 'is-over');
    }
    this.paintHud();
  };

  BullittGrid.prototype.tuneLevel = function () {
    var lvl = this.level;
    // Accelerating curve: barely faster for the first few rounds, then it bites.
    this.stepMs = Math.max(
      this.cfg.rampFloor,
      Math.round(this.cfg.stepMs - this.cfg.rampSpeed * Math.pow(lvl, this.cfg.rampCurve))
    );
    this.oppMistake = Math.max(0.03, this.cfg.aiMistake * Math.pow(this.cfg.rampSharp, lvl));
    // Vision opens up as the streak grows: near-sighted at first, then it
    // starts genuinely planning ahead.
    this.oppSight = Math.min(400, Math.round(this.cfg.aiSight * Math.pow(this.cfg.aiSightRamp, lvl)));
  };

  BullittGrid.prototype.startRound = function (human) {
    if (!this.booted) { this.buildField(); this.booted = true; }
    this.tuneLevel();

    this.grid.fill(0);
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    if (this.gctx) this.gctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    this.p1 = new Rider(this.cfg.playerColor, human, this.cfg.aiMistake, 400);
    this.p2 = new Rider(this.cfg.aiColor, false,
      human ? this.oppMistake : this.cfg.aiMistake,
      human ? this.oppSight : 400);

    this.p1.x = Math.round(this.cols * 0.22);
    this.p1.y = Math.round(this.rows * 0.35);
    this.p1.dir = 'right';

    this.p2.x = Math.round(this.cols * 0.78);
    this.p2.y = Math.round(this.rows * 0.65);
    this.p2.dir = 'left';

    this.occupy(this.p1, 1);
    this.occupy(this.p2, 2);

    // A round is defined by its seed. Supply one (a server will, for a verified
    // run) or take a random one for casual play.
    this.seed = (this.pendingSeed != null) ? this.pendingSeed
              : (Math.random() * 0x7FFFFFFF) | 0;
    this.pendingSeed = null;
    this.rng = mulberry32(this.seed);
    this.inputLog = [];
    if (this._chain === undefined) { this._chain = 2166138321; this._shadowStreak = 0; this._shadowLevel = 0; }
    this._fold(this.seed);

    this.steps = 0;
    this.maxSteps = Math.round(this.cfg.maxSeconds * 1000 / this.stepMs);
    this.pickups = [];
    this.phaseLeft = 0;
    this.boostLeft = 0;
    this.aiPhaseLeft = 0;
    this.aiBoostLeft = 0;
    this.aiAcc = 0;
    this.curInterval = this.stepMs;
    if (this.fx) this.fx.clearRect(0, 0, this.fxCanvas.width, this.fxCanvas.height);

    this.root.classList.add('is-ready', 'is-running');
    this.root.classList.remove('is-over');
    this.root.classList.toggle('is-playing', !!human);
    this.root.classList.toggle('is-attract', !human);
    if (this.overCard) this.overCard.hidden = true;
    if (this.poster) this.poster.classList.add('is-hidden');

    if (human) {
      this.mode = 'countdown';
      this.countdown = this.streak === 0 ? 3 : 1;
      this.showCount(String(this.countdown));
    } else {
      this.mode = 'attract';
      this.showCount('');
    }

    this.acc = 0;
    this.last = 0;
    this.paintHud();
    this.paint();
    this.run();
  };

  BullittGrid.prototype.endRound = function (result) {
    this.lastOutcome = result;
    var self = this;
    this.stop();

    if (!this.p1.isHuman) {
      this.mode = 'over';
      setTimeout(function () { if (self.mode === 'over') self.startRound(false); }, 1400);
      return;
    }

    // A draw is nobody's fault — replay the level rather than end a run on it.
    if (result === 'draw') {
      this.mode = 'over';
      this.showCount('REMATCH');
      setTimeout(function () {
        if (self.mode !== 'over') return;
        self.showCount('');
        self.startRound(true);
      }, 900);
      return;
    }

    if (result === 'win') {
      this.streak++;
      this.level++;
      this._shadowStreak++;
      this._shadowLevel++;
      this._fold(0x571EA0 ^ this.steps);
      this.mode = 'over';
      this.paintHud();
      this.showCount(this.streak + ' IN A ROW');
      setTimeout(function () {
        if (self.mode !== 'over') return;
        self.showCount('');
        self.startRound(true);
      }, 1100);
      return;
    }

    // One shot at a crit save before the run is called.
    if (result === 'lose' && this.cfg.critOn && this.p1.trail.length > 4 &&
        !this.critExhausted()) {
      this.startCritSave(result);
      return;
    }

    this.mode = 'over';
    this.root.classList.remove('is-playing');
    this.root.classList.add('is-over');
    this.endRun(result);
  };

  BullittGrid.prototype.endRun = function (result) {
    var isBest = this.streak > this.best && this.streak > 0;
    if (isBest) { this.best = this.streak; this.writeBest(this.best); }
    this.paintBest();
    this.setStatus('');

    if (this.overCard) {
      this.overTitle.textContent = isBest ? 'NEW PERSONAL BEST'
        : (this.streak > 0 ? 'STREAK OVER' : 'WIPEOUT');
      this.overBody.textContent = this.streak === 0
        ? 'The grid got you on round one.'
        : this.streak + (this.streak === 1 ? ' win' : ' wins') + ' in a row.';

      if (this.reward) this.reward.hidden = !(this.streak > 0);
      if (this.newBestEl) this.newBestEl.hidden = !isBest;

      // Nothing to share if you never won a round.
      if (this.shareBtn) this.shareBtn.hidden = this.streak < 1;
      if (this.saveBtn) this.saveBtn.hidden = this.streak < 1;

      this.overCard.hidden = false;
      if (this.retryBtn) this.retryBtn.focus();
    }

    // Walk away and the wordmark takes the hero back over.
    this.armIdleTimer();
  };

  /* ---------- personal best ---------- */

  BullittGrid.prototype.paintBest = function () {
    if (this.bestEl) this.bestEl.textContent = this.best;
  };
  /* Stored as "value.digest". Editing the number by hand leaves the digest
     stale and the value is discarded rather than shown. The salt is sitting in
     this file, so this stops idle poking and nothing more — the personal best is
     a local nicety, never evidence. */
  function bestDigest(v) {
    var h = 2166138321 >>> 0, str = 'lvh:' + v + ':bullitt';
    for (var i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619) >>> 0;
    }
    return h.toString(36);
  }
  BullittGrid.prototype.readBest = function () {
    try {
      var raw = window.localStorage.getItem(STORE_KEY);
      if (!raw) return 0;
      var parts = String(raw).split('.');
      var v = parseInt(parts[0], 10) || 0;
      if (parts.length !== 2 || parts[1] !== bestDigest(v)) return 0;
      return v;
    } catch (e) { return 0; }
  };
  BullittGrid.prototype.writeBest = function (v) {
    try { window.localStorage.setItem(STORE_KEY, v + '.' + bestDigest(v)); } catch (e) {}
  };

  BullittGrid.prototype.setStatus = function (msg) {
    if (this.statusEl) this.statusEl.textContent = msg;
  };

  /* ---------- share card ----------
     A square JPEG built entirely on the client. No upload, no server, no
     account — the image never leaves the device unless the visitor shares it. */

  BullittGrid.prototype.buildShareCanvas = function () {
    var S = 1080;
    var c = document.createElement('canvas');
    c.width = S; c.height = S;
    var x = c.getContext('2d');

    // Background + grid, matching the hero.
    x.fillStyle = this.cfg.bgColor;
    x.fillRect(0, 0, S, S);
    x.strokeStyle = this.cfg.gridColor;
    x.lineWidth = 2;
    for (var i = 0; i <= S; i += 60) {
      x.beginPath(); x.moveTo(i + 0.5, 0); x.lineTo(i + 0.5, S); x.stroke();
      x.beginPath(); x.moveTo(0, i + 0.5); x.lineTo(S, i + 0.5); x.stroke();
    }

    // The final grid state, scaled by a whole number so pixels stay square.
    x.imageSmoothingEnabled = false;
    var gw = this.canvas.width, gh = this.canvas.height;
    var scale = Math.max(1, Math.floor(Math.min((S - 120) / gw, (S * 0.34) / gh)));
    var dw = gw * scale, dh = gh * scale;
    var dx = Math.round((S - dw) / 2), dy = Math.round(S * 0.52);
    x.globalAlpha = 0.9;
    x.drawImage(this.canvas, dx, dy, dw, dh);
    x.globalAlpha = 1;

    x.textAlign = 'center';
    var sans = 'ui-sans-serif, system-ui, -apple-system, "Helvetica Neue", Arial, sans-serif';

    x.fillStyle = this.cfg.playerColor;
    x.font = '600 30px ' + sans;
    x.fillText(this.cfg.shareTitle.toUpperCase(), S / 2, 120);

    x.fillStyle = '#ffffff';
    x.font = '800 260px ' + sans;
    x.fillText(String(this.streak), S / 2, 380);

    x.font = '700 38px ' + sans;
    x.fillText(this.cfg.shareText.toUpperCase(), S / 2, 445);

    x.fillStyle = this.cfg.playerColor;
    x.font = '700 34px ' + sans;
    x.fillText(this.cfg.shareTag, S / 2, S - 92);

    x.fillStyle = 'rgba(255,255,255,.55)';
    x.font = '500 26px ' + sans;
    x.fillText(this.cfg.shareSite, S / 2, S - 48);

    return c;
  };

  BullittGrid.prototype.cardBlob = function () {
    var c = this.buildShareCanvas();
    return new Promise(function (resolve, reject) {
      if (!c.toBlob) { reject(new Error('unsupported')); return; }
      c.toBlob(function (b) { b ? resolve(b) : reject(new Error('empty')); }, 'image/jpeg', 0.92);
    });
  };

  BullittGrid.prototype.fileName = function () {
    return 'miss-bullitt-' + this.streak + '-in-a-row.jpg';
  };

  /* Can this document actually perform a download?
     Inside an iframe the answer is often no: Shopify's theme editor previews
     the storefront in one, and a sandboxed frame both blocks downloads and
     gives blob: URLs an opaque origin, so clicking the link navigates to
     something unreachable — the browser's "page couldn't be reached". iOS
     Safari ignores `download` on blob URLs in several versions too. */
  BullittGrid.prototype.canDownload = function () {
    if (!('download' in document.createElement('a'))) return false;
    try { if (window.self !== window.top) return false; } catch (e) { return false; }
    return true;
  };

  BullittGrid.prototype.coarse = function () {
    return !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
  };

  /* Put the card in the page as a plain <img> from a data: URL.
     No navigation, no blob URL, no sandbox involvement — this works
     everywhere, including the theme editor and mobile Safari, where
     long-press-to-save is the native gesture anyway. Built in JS so the
     section markup does not need to change. */
  BullittGrid.prototype.showCardInline = function (dataUrl) {
    var panel = this.overCard && this.overCard.querySelector('.lvhg-panel');
    if (!panel || !dataUrl) return false;
    var old = panel.querySelector('[data-lvh-cardimg]');
    if (old && old.parentNode) old.parentNode.removeChild(old);
    var img = document.createElement('img');
    img.setAttribute('data-lvh-cardimg', '');
    img.src = dataUrl;
    img.alt = 'Your score card';
    img.style.cssText =
      'display:block;width:100%;max-width:300px;margin:14px auto 0;' +
      'image-rendering:pixelated;border:0;';
    panel.appendChild(img);
    this.setStatus(this.coarse()
      ? 'Long-press the card to save it, then tag us.'
      : 'Right-click the card to save it, then tag us.');
    return true;
  };

  /* Save: show the card, and download it too where the browser allows. */
  BullittGrid.prototype.saveCard = function () {
    var self = this;
    this.setStatus('Building your card\u2026');

    var canvas, shown = false;
    try {
      canvas = this.buildShareCanvas();
      shown = this.showCardInline(canvas.toDataURL('image/jpeg', 0.92));
    } catch (e) { /* fall through to the download attempt */ }

    if (!this.canDownload()) {
      if (!shown) this.setStatus('Could not build the image on this browser.');
      return;                                   // inline is the whole answer here
    }
    if (!canvas || !canvas.toBlob) {
      if (!shown) this.setStatus('Could not build the image on this browser.');
      return;
    }
    canvas.toBlob(function (blob) {
      if (!blob) { if (!shown) self.setStatus('Could not build the image.'); return; }
      try {
        var url = URL.createObjectURL(blob);
        var el = document.createElement('a');
        el.href = url;
        el.download = self.fileName();
        el.rel = 'noopener';
        document.body.appendChild(el);
        el.click();
        document.body.removeChild(el);
        setTimeout(function () { URL.revokeObjectURL(url); }, 4000);
        self.setStatus('Saved. Tag us when you post it.');
      } catch (e) {
        if (!shown) self.setStatus('Could not save on this browser.');
      }
    }, 'image/jpeg', 0.92);
  };

  /* Share: hands the JPEG to the OS share sheet, which is how a phone gets it
     into Messages, Mail, WhatsApp or Instagram. Falls back to download. */
  BullittGrid.prototype.shareCard = function () {
    var self = this;
    this.setStatus('Building your card…');

    this.cardBlob().then(function (blob) {
      var file = null;
      try {
        file = new File([blob], self.fileName(), { type: 'image/jpeg' });
      } catch (e) { /* File constructor unavailable */ }

      var payload = {
        title: self.cfg.shareTitle,
        text: self.streak + ' ' + self.cfg.shareText + ' ' + self.cfg.shareTag
      };
      if (file) payload.files = [file];

      if (navigator.share && (!file || !navigator.canShare || navigator.canShare({ files: [file] }))) {
        return navigator.share(payload).then(function () {
          self.setStatus('Shared. Thanks for tagging us.');
        }).catch(function (err) {
          // AbortError just means they closed the sheet — not a failure.
          if (!err || err.name !== 'AbortError') self.saveCard();
          else self.setStatus('');
        });
      }
      self.saveCard();
    }).catch(function () {
      self.setStatus('Could not build the image on this browser.');
    });
  };

  /* ---------- loop ---------- */

  BullittGrid.prototype.run = function () {
    var self = this;
    if (this.raf) return;
    var tick = function (ts) {
      self.raf = requestAnimationFrame(tick);

      // Logo mode redraws every frame so the construction fragments animate
      // smoothly, while the rider still advances on the step clock.
      if (self.mode === 'logo') {
        if (!self.last) { self.last = ts; return; }
        var ld = ts - self.last;
        self.last = ts;
        if (ld > 400) return;

        if (self.logoPhase === 'out') {
          if (self.paintDissolve(ts)) self.startLogo();
          return;
        }
        if (self.logoDone) {
          if (ts - self.logoDoneAt >= self.cfg.logoHoldMs) self.beginDissolve(ts);
          else self.paintLogo(ts);
          return;
        }
        self.logoAcc += ld;
        while (self.logoAcc >= self.cfg.logoStepMs && !self.logoDone) {
          self.logoAcc -= self.cfg.logoStepMs;
          self.stepLogo(ts);
        }
        self.paintLogo(ts);
        return;
      }

      // Crit-save bar: pure UI, the world is frozen behind it.
      if (self.mode === 'crit') {
        if (self.critExpired(ts)) { self.critLocked = -1; self.critResolve(false); }
        self.paintFx(ts);
        return;
      }

      // Rewind: un-draw both trails over critRewindMs, then count back in.
      if (self.mode === 'rewind') {
        var prog = (ts - self.rewindT0) / (self.rewindMs || self.cfg.critRewindMs);
        var want = Math.min(self.rewindFrom, Math.floor(prog * self.rewindFrom));
        while (self.rewindDone < want) self.stepRewind();
        /* Rebuild the glow from the trail canvas every frame. stepRewind clears
           cells on the trail canvas, but the glow layer is a separate blurred
           copy that was only refreshed by paintAll — which never runs during a
           rewind. So the erased trail stayed on screen as a soft blurred ghost
           of the exact path being undone. */
        if (self.gctx) {
          self.gctx.clearRect(0, 0, self.canvas.width, self.canvas.height);
          self.gctx.drawImage(self.canvas, 0, 0);
        }
        self.paintFx(ts);
        // Let the debris finish before counting back in, or the last stretch
        // of trail would be cut off mid-disintegration.
        if (prog >= 1 && !(self.rewindFx && self.rewindFx.length)) { self.finishRewind(); }
        return;
      }

      if (self.mode !== 'attract' && self.mode !== 'playing' && self.mode !== 'countdown') return;
      if (!self.last) { self.last = ts; return; }
      var dt = ts - self.last;
      self.last = ts;
      if (dt > 400) { self.acc = 0; return; }

      self.acc += dt;
      // The tick runs at whatever the PLAYER's current speed is. The opponent
      // banks that elapsed time and moves on its own interval, so a boost
      // speeds up only the rider holding it.
      var interval = self.mode === 'countdown' ? 700
        : (self.boostLeft > 0 ? self.stepMs * self.cfg.boostScale : self.stepMs);
      self.curInterval = interval;
      while (self.acc >= interval) {
        self.acc -= interval;
        if (self.mode === 'countdown') self.tickCountdown();
        else self.step();
        if (self.mode === 'over') { self.acc = 0; break; }
      }
    };
    this.raf = requestAnimationFrame(tick);
  };

  BullittGrid.prototype.stop = function () {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = null;
    this.last = 0;
  };

  BullittGrid.prototype.tickCountdown = function () {
    this.countdown--;
    if (this.countdown > 0) { this.showCount(String(this.countdown)); return; }
    this.showCount('RIDE');
    var self = this;
    this.mode = 'playing';
    setTimeout(function () { if (self.mode === 'playing') self.showCount(''); }, 400);
  };

  BullittGrid.prototype.showCount = function (txt) {
    if (!this.countEl) return;
    this.countEl.textContent = txt;
    this.countEl.hidden = !txt;
  };

  BullittGrid.prototype.paintHud = function () {
    if (this.streakEl) this.streakEl.textContent = this.streak;
    if (this.levelEl) this.levelEl.textContent = this.level + 1;
  };

  /* ---------- simulation ---------- */

  BullittGrid.prototype.idx = function (x, y) { return y * this.cols + x; };

  BullittGrid.prototype.blocked = function (x, y) {
    return x < 0 || y < 0 || x >= this.cols || y >= this.rows || this.grid[this.idx(x, y)] !== 0;
  };

  BullittGrid.prototype.occupy = function (r, mark) {
    this.grid[this.idx(r.x, r.y)] = mark;
    r.trail.push([r.x, r.y]);
  };

  /* Draw from the seeded stream. Falls back to Math.random only before a round
     has been started, so nothing can throw. */
  /* TAMPER EVIDENCE.
     Every scoring event folds into a hash chain, and the streak is also kept in
     a shadow counter that only endRound touches. Setting __lvhGame.streak = 999
     from the console changes the displayed number but neither the chain nor the
     shadow, so the two disagree and the run is marked tampered.

     Be clear about what this is and isn't: it makes casual edits VISIBLE, it
     does not make them impossible. Anyone willing to patch this function defeats
     it, because it runs on their machine. It exists to flag the console-edit
     case and to travel with the replay; the only thing that actually decides a
     score is the server recomputing it. */
  BullittGrid.prototype._fold = function (v) {
    var h = this._chain >>> 0;
    h ^= (v >>> 0);
    h = Math.imul(h, 16777619) >>> 0;
    this._chain = h;
  };

  BullittGrid.prototype.verifyIntegrity = function () {
    return this.streak === this._shadowStreak && this.level === this._shadowLevel;
  };

  BullittGrid.prototype.rnd = function () {
    return this.rng ? this.rng() : Math.random();
  };

  BullittGrid.prototype.queueTurn = function (r, dir) {
    if (!r || dir === OPP[r.dir] || dir === r.dir) return;
    r.queued = dir;
    // Log the human's inputs against the step index, which IS the clock here —
    // the simulation advances in fixed steps, so no wall-clock time is needed
    // to replay it.
    if (r.isHuman && this.inputLog) {
      this.inputLog.push([this.steps, dir]);
      this._fold((this.steps << 3) ^ ORDER.indexOf(dir));
    }
  };

  /* Everything a verifier needs to recompute this round from scratch. */
  BullittGrid.prototype.getReplay = function () {
    return {
      seed: this.seed,
      inputs: (this.inputLog || []).slice(),
      steps: this.steps,
      streak: this.streak,
      level: this.level,
      chain: this._chain >>> 0,
      critTries: this.critTries || 0,
      critHits: this.critHits || 0,
      critPerfect: this.critPerfect || 0,
      tampered: !this.verifyIntegrity()
    };
  };

  BullittGrid.prototype.step = function () {
    var p1 = this.p1, p2 = this.p2;

    this.steps++;
    if (this.steps > this.maxSteps) { this.endRound('draw'); return; }

    // Opponent cadence: it banks the tick's elapsed time and only moves once
    // it has enough for its OWN interval. Player boosts therefore don't drag
    // the opponent along with them.
    var aiInterval = this.aiBoostLeft > 0 ? this.stepMs * this.cfg.boostScale : this.stepMs;
    this.aiAcc = (this.aiAcc || 0) + (this.curInterval || this.stepMs);
    var p2moves = this.aiAcc >= aiInterval - 0.001;
    if (p2moves) this.aiAcc -= aiInterval;

    if (p1.isHuman) {
      if (p1.queued) { p1.dir = p1.queued; p1.queued = null; }
    } else {
      p1.dir = this.chooseAi(p1);
    }
    if (p2moves) p2.dir = this.chooseAi(p2);

    var a = VEC[p1.dir], b = VEC[p2.dir];
    var nax = p1.x + a[0], nay = p1.y + a[1];
    var nbx = p2moves ? p2.x + b[0] : p2.x;
    var nby = p2moves ? p2.y + b[1] : p2.y;

    // Phasing lets the player pass through the OPPONENT's trail only —
    // walls and their own trail still end the run.
    // Phasing passes through EVERY trail, your own included. Only the walls
    // still stop you — otherwise it isn't really a get-out-of-jail card.
    var phasing = p1.isHuman && this.phaseLeft > 0;
    var aDead = phasing
      ? (nax < 0 || nay < 0 || nax >= this.cols || nay >= this.rows)
      : this.blocked(nax, nay);
    var aiPhasing = this.aiPhaseLeft > 0;
    var bDead = !p2moves ? false
      : (aiPhasing
          ? (nbx < 0 || nby < 0 || nbx >= this.cols || nby >= this.rows)
          : this.blocked(nbx, nby));
    var headOn = p2moves && nax === nbx && nay === nby;

    if (headOn || (aDead && bDead)) { this.endRound('draw'); return; }
    if (aDead) { this.endRound('lose'); return; }
    if (bDead) { this.endRound('win'); return; }

    p1.x = nax; p1.y = nay; this.occupy(p1, 1);
    if (p2moves) { p2.x = nbx; p2.y = nby; this.occupy(p2, 2); }

    if (p1.isHuman) {
      if (this.phaseLeft > 0) this.phaseLeft--;
      if (this.boostLeft > 0) this.boostLeft--;
      this.takePickup(nax, nay);
      if (this.cfg.phaseOn && this.steps % this.cfg.phaseEvery === 0) this.spawnPickup();
    }
    if (p2moves) {
      if (this.aiPhaseLeft > 0) this.aiPhaseLeft--;
      if (this.aiBoostLeft > 0) this.aiBoostLeft--;
      if (p1.isHuman && this.cfg.aiUsesPowerups) this.aiTakePickup(nbx, nby);
    }

    this.paint();
  };

  /* The opponent grabs power-ups too, and spends them on the spot — it has no
     reason to hold one back the way a player might. */
  BullittGrid.prototype.aiTakePickup = function (x, y) {
    for (var i = 0; i < this.pickups.length; i++) {
      var p = this.pickups[i];
      for (var k = 0; k < CROSS.length; k++) {
        if (p.x + CROSS[k][0] === x && p.y + CROSS[k][1] === y) {
          if (p.kind === 'phase') this.aiPhaseLeft = this.cfg.phaseSteps;
          else this.aiBoostLeft = this.cfg.boostSteps;
          this.pickups.splice(i, 1);
          return true;
        }
      }
    }
    return false;
  };

  /* The opponent is meant to be beatable, not suicidal. It scores each option
     by reachable space; a "mistake" then picks a DIFFERENT option at random —
     but only from ones that are still survivable. Previously a mistake could
     pick any unblocked move, including one straight into a pocket, so early
     rounds were full of the AI killing itself for no reason. */
  /* Survival is never the difficulty dial.
     Earlier versions made "easy" mean the opponent drove into things — first by
     random mistakes, then by short sight. Both read as a stupid opponent
     rather than a beatable one.
     Now it ALWAYS sees far enough to avoid trapping itself, and difficulty
     comes from how hard it plays for territory (planning weight) and how hard
     it hunts you (aggression). Early on it survives comfortably but wanders;
     later it fights for space and cuts you off. */
  BullittGrid.prototype.chooseAi = function (r) {
    var options = [r.dir, leftOf(r.dir), rightOf(r.dir)];
    var cap = Math.min(r.sight || 400, this.cols * this.rows);
    var viable = [];
    var best = null, bestScore = -1, bestSpace = 0;

    // Intent, only for the opponent and only against a human.
    var hunting = (r === this.p2) && this.p1 && this.p1.isHuman;
    var goal = hunting ? this.nearestPickup(r) : null;
    var cut = hunting ? this.interceptPoint() : null;
    var blockW = hunting
      ? this.cfg.aiBlock + this.cfg.aiBlockRamp * this.level
      : 0;

    // Planning weight: how much it actually cares about keeping room. Low
    // early (it wanders safely), high later (it plays territory properly).
    var planW = hunting
      ? Math.min(1, this.cfg.aiPlan + this.cfg.aiPlanRamp * this.level)
      : 1;

    for (var i = 0; i < options.length; i++) {
      var d = options[i], v = VEC[d];
      var nx = r.x + v[0], ny = r.y + v[1];
      if (this.blocked(nx, ny)) continue;
      var sp = this.openSpace(nx, ny, cap);
      viable.push({ dir: d, space: sp, exits: this.exitsFrom(nx, ny), x: nx, y: ny });
      if (sp > bestSpace) bestSpace = sp;
    }
    if (!viable.length) return r.dir;                  // boxed in; ride it out

    /* TWO STAGES, and the order is the entire point.
       Aggression must never be able to outbid survival. Previously hunting was
       just another term in one big sum, and because the distance gradient is
       unbounded it dwarfed every safety term — so the opponent would line up on
       you and drive straight into the wall behind you. Scoring cannot fix that;
       a big enough bonus always wins. So decide what is SAFE first, then play as
       aggressively as possible strictly inside that set. */
    var floor = Math.max(this.cfg.aiMinSpace, bestSpace * this.cfg.aiSafeFrac);
    var safe = [];
    for (i = 0; i < viable.length; i++) {
      if (viable[i].exits > 0 && viable[i].space >= floor) safe.push(viable[i]);
    }
    if (!safe.length) {                                // nothing comfortable
      var room = viable[0];
      for (i = 1; i < viable.length; i++) if (viable[i].space > room.space) room = viable[i];
      return room.dir;
    }

    // Within survivable moves only, play for position and for you.
    bestScore = -Infinity;
    for (i = 0; i < safe.length; i++) {
      var c = safe[i];
      var sc = (c.space / Math.max(1, cap)) * 20 * planW
             + (c.dir === r.dir ? 6 : 0)
             + this.rnd() * 4;
      if (goal) {
        sc += Math.max(0, 22 - (Math.abs(c.x - goal.x) + Math.abs(c.y - goal.y))) * this.cfg.aiSeek;
      }
      if (cut && blockW > 0) {
        sc -= (Math.abs(c.x - cut.x) + Math.abs(c.y - cut.y)) * blockW * this.cfg.aiHuntGain;
      }
      if (sc > bestScore) { bestScore = sc; best = c.dir; }
    }
    if (!best) best = safe[0].dir;

    // A "mistake" may only ever swap to another move from the SAFE set.
    if (safe.length > 1 && this.rnd() < r.mistake) {
      var alt = [];
      for (i = 0; i < safe.length; i++) if (safe[i].dir !== best) alt.push(safe[i].dir);
      if (alt.length) best = alt[Math.floor(this.rnd() * alt.length)];
    }
    return best;
  };

  /* How many ways out a cell has once you're standing on it. Zero means the
     move is a self-made dead end. */
  BullittGrid.prototype.exitsFrom = function (x, y) {
    var n = 0;
    for (var k = 0; k < 4; k++) {
      var v = VEC[ORDER[k]];
      if (!this.blocked(x + v[0], y + v[1])) n++;
    }
    return n;
  };

  /* Closest pickup worth detouring for. Anything far is ignored so the
     opponent doesn't abandon a good line to cross the whole board. */
  BullittGrid.prototype.nearestPickup = function (r) {
    if (!this.pickups || !this.pickups.length) return null;
    var best = null, bd = 1e9;
    for (var i = 0; i < this.pickups.length; i++) {
      var p = this.pickups[i];
      var d = Math.abs(p.x - r.x) + Math.abs(p.y - r.y);
      if (d < bd) { bd = d; best = p; }
    }
    return bd <= 26 ? best : null;
  };

  /* A point AHEAD of the player along its heading — where it's going, not
     where it is. Aiming here is what makes the opponent feel like it's cutting
     you off rather than trailing you. */
  BullittGrid.prototype.interceptPoint = function () {
    var p = this.p1;
    if (!p) return null;
    var v = VEC[p.dir];
    var lead = 6;
    return {
      x: Math.max(0, Math.min(this.cols - 1, p.x + v[0] * lead)),
      y: Math.max(0, Math.min(this.rows - 1, p.y + v[1] * lead))
    };
  };

  BullittGrid.prototype.openSpace = function (x, y, cap) {
    this._gen++;
    if (this._gen > 65000) { this._stamp.fill(0); this._gen = 1; }
    var gen = this._gen;
    var stack = [this.idx(x, y)];
    this._stamp[stack[0]] = gen;
    var n = 0;

    while (stack.length && n < cap) {
      var i = stack.pop();
      n++;
      var cx = i % this.cols;
      var cy = (i - cx) / this.cols;
      for (var k = 0; k < 4; k++) {
        var v = VEC[ORDER[k]];
        var ax = cx + v[0], ay = cy + v[1];
        if (ax < 0 || ay < 0 || ax >= this.cols || ay >= this.rows) continue;
        var ai = ay * this.cols + ax;
        if (this._stamp[ai] === gen || this.grid[ai] !== 0) continue;
        this._stamp[ai] = gen;
        stack.push(ai);
      }
    }
    return n;
  };

  /* ---------- effects layer ----------
     Cleared every frame, so nothing here can stain the trail canvas. */

  BullittGrid.prototype.paintFx = function (now) {
    if (!this.fx) return;
    var W = this.fxCanvas.width, H = this.fxCanvas.height, S = CELL;
    this.fx.clearRect(0, 0, W, H);

    // Rewind debris. Cells wait at full colour, then scatter and fade.
    if (this.rewindFx && this.rewindFx.length) {
      var life = this.cfg.rewindFxMs, sp = this.cfg.rewindScatter;
      for (var ri = this.rewindFx.length - 1; ri >= 0; ri--) {
        var f = this.rewindFx[ri];
        var ft = (now - f.born) / life;
        this.fx.fillStyle = f.col;
        if (ft <= 0) {                       // still holding, drawn solid
          this.fx.globalAlpha = 1;
          this.fx.fillRect(f.x * S, f.y * S, S, S);
          continue;
        }
        if (ft >= 1) { this.rewindFx.splice(ri, 1); continue; }
        var fe = ft * ft;                    // accelerate away
        this.fx.globalAlpha = 1 - ft;
        this.fx.fillRect(
          Math.round(f.x * S + f.vx * sp * S * fe),
          Math.round(f.y * S + f.vy * sp * S * fe),
          S, S
        );
      }
      this.fx.globalAlpha = 1;
    }

    // Pickups sit inside a pixelated ring so they read as collectables rather
    // than stray trail. Phase = dashed ring, boost = ring with chevrons.
    var i, p;
    for (i = 0; i < this.pickups.length; i++) {
      p = this.pickups[i];
      this.fx.globalAlpha = 0.78 + 0.22 * Math.sin((now - p.born) / 320);
      this.paintRing(p, S, now);
      this.fx.globalAlpha = 1;
    }

    // Phase active: dashes trailing off the player's nose.
    if (this.phaseLeft > 0 && this.p1) {
      this.fx.fillStyle = this.cfg.phaseColor;
      this.fx.globalAlpha = 0.35 + 0.4 * Math.sin(now / 90);
      var t = this.p1.trail, n = t.length;
      for (i = 1; i < 10 && n - i * 2 >= 0; i++) {
        var c = t[n - i * 2];
        if (c) this.fx.fillRect(c[0] * S + 1, c[1] * S + 1, S - 2, S - 2);
      }
      this.fx.globalAlpha = 1;
    }

    if (this.boostLeft > 0 && this.p1) {
      this.fx.fillStyle = this.cfg.boostColor;
      var bt = this.p1.trail, bn = bt.length;
      for (i = 1; i < 7 && bn - i >= 0; i++) {
        var bc = bt[bn - i];
        if (!bc) continue;
        this.fx.globalAlpha = 0.5 * (1 - i / 7);
        this.fx.fillRect(bc[0] * S, bc[1] * S, S, S);
      }
      this.fx.globalAlpha = 1;
    }

    if (this.aiBoostLeft > 0 && this.p2) {
      this.fx.fillStyle = this.cfg.boostColor;
      var at = this.p2.trail, an = at.length;
      for (i = 1; i < 7 && an - i >= 0; i++) {
        var ac = at[an - i];
        if (!ac) continue;
        this.fx.globalAlpha = 0.4 * (1 - i / 7);
        this.fx.fillRect(ac[0] * S, ac[1] * S, S, S);
      }
      this.fx.globalAlpha = 1;
    }
    if (this.aiPhaseLeft > 0 && this.p2) {
      this.fx.fillStyle = this.cfg.phaseColor;
      this.fx.globalAlpha = 0.3 + 0.3 * Math.sin(now / 90);
      var pt = this.p2.trail, pn = pt.length;
      for (i = 1; i < 8 && pn - i * 2 >= 0; i++) {
        var pc = pt[pn - i * 2];
        if (pc) this.fx.fillRect(pc[0] * S + 1, pc[1] * S + 1, S - 2, S - 2);
      }
      this.fx.globalAlpha = 1;
    }

    if (this.mode === 'crit') this.paintCritBar(now, W, H);
  };

  /* Five cells in a cross — the whole shape is the hitbox, so brushing any
     arm collects it. Small on purpose: the old ring read as scenery. */
  BullittGrid.prototype.paintRing = function (p, S, now) {
    var f = this.fx;
    // Both types draw the SAME solid cross and are told apart by colour alone.
    // The phase icon used to blink its arms individually, which at five pixels
    // read as a rendering fault rather than a style.
    f.fillStyle = p.kind === 'boost' ? this.cfg.boostColor : this.cfg.phaseColor;
    for (var k = 0; k < CROSS.length; k++) {
      var c = CROSS[k];
      f.fillRect((p.x + c[0]) * S, (p.y + c[1]) * S, S, S);
    }
  };

  BullittGrid.prototype.paintCritBar = function (now, W, H) {
    var f = this.fx;
    var bw = Math.round(W * 0.5), bh = Math.max(6, Math.round(H * 0.035));
    var bx = Math.round((W - bw) / 2), by = Math.round(H * 0.58);

    // Same stepped pixel panel as the tutorial card, drawn on canvas: dark
    // fill, hard edge in the player colour, no radius, no anti-aliasing.
    var pad = Math.max(8, Math.round(bh * 0.9));
    var e = Math.max(2, Math.round(bh * 0.22));
    var px = bx - pad, py = by - pad, pw = bw + pad * 2, ph = bh + pad * 2;
    f.fillStyle = this.cfg.playerColor;
    f.fillRect(px - e, py - e, pw + e * 2, ph + e * 2);
    f.fillStyle = '#0b0b14';
    f.fillRect(px, py, pw, ph);

    // the zone you have to land in
    var half = this.critZone / 2;
    var zx = Math.round(bx + (this.critCentre - half) * bw);
    var zw = Math.max(2, Math.round(this.critZone * bw));
    f.fillStyle = this.cfg.playerColor;
    f.globalAlpha = 0.85;
    f.fillRect(zx, by, zw, bh);
    f.globalAlpha = 1;

    // the track
    f.fillStyle = 'rgba(255,255,255,.35)';
    f.fillRect(bx, by + Math.floor(bh / 2) - 1, bw, 2);

    // the marker — frozen where it was locked
    var pos = this.critLocked !== null ? this.critLocked : this.critMarker(now);
    var mx = Math.round(bx + pos * bw);
    var hit = Math.abs(pos - this.critCentre) <= half;
    f.fillStyle = this.critLocked === null ? '#ffffff' : (hit ? this.cfg.phaseColor : '#ff4444');
    f.fillRect(mx - 1, by - 5, 3, bh + 10);
  };

  /* ---------- rendering ---------- */

  BullittGrid.prototype.paint = function () {
    this.paintRider(this.p1);
    this.paintRider(this.p2);
    if (this.fx) this.paintFx(typeof performance !== 'undefined' ? performance.now() : Date.now());
    if (this.gctx) {
      this.gctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.gctx.drawImage(this.canvas, 0, 0);
    }
  };

  BullittGrid.prototype.paintRider = function (r) {
    var ctx = this.ctx, t = r.trail, n = t.length;
    var dim = mix(r.color, '#000000', this.cfg.trailDim);

    for (var k = 1; k <= 3; k++) {
      var c = t[n - 1 - k];
      if (c) { ctx.fillStyle = dim; ctx.fillRect(c[0] * CELL, c[1] * CELL, CELL, CELL); }
    }
    var body = t[n - 2];
    if (body) { ctx.fillStyle = r.color; ctx.fillRect(body[0] * CELL, body[1] * CELL, CELL, CELL); }
    var head = t[n - 1];
    if (head) { ctx.fillStyle = r.light; ctx.fillRect(head[0] * CELL, head[1] * CELL, CELL, CELL); }
  };

  /* ---------- init ---------- */

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-lvh-game]').forEach(function (el) {
      if (el.__lvhGame) return;
      el.__lvhGame = new BullittGrid(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });

  window.LvhPixelGame = { init: initAll };
})();
